import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useLanguage } from '../context/LanguageContext';
import { generateWorkbookPDF } from '../utils/generateWorkbookPDF';
import Auth from './Auth';
import supabase from '../supabase/supabase';

const { FiDownload, FiLoader, FiCheckCircle, FiFileText, FiStar, FiX, FiLock } = FiIcons;

// ── Labels ──────────────────────────────────────────────────────────────────
const LABELS = {
  en: {
    badge: 'Free Download',
    title: 'Download Your Free Workbook',
    subtitle: 'The complete 10-step PDF workbook — print it, fill it in, and build your business offline too.',
    features: [
      '10 structured step pages',
      'Interactive tools & prompts',
      'Action items with checkboxes',
      'Notes & reflection pages',
    ],
    btn: 'Download PDF Workbook',
    btnDone: 'Downloaded!',
    btnLoading: 'Generating PDF…',
    guestBtn: 'Get Free Workbook',
    guestHint: 'Free registration required — takes 30 seconds',
    pages: '14 pages · A4 · Print-ready',
    modalTitle: 'Get Your Free Workbook',
    modalSubtitle: 'Create a free account to download the 10-step workbook instantly.',
    downloadLabel: 'Download',
    signupLabel: 'Sign up free',
  },
  de: {
    badge: 'Kostenloser Download',
    title: 'Lade dein kostenloses Workbook herunter',
    subtitle: 'Das vollständige 10-Schritte-PDF-Workbook — drucke es aus, fülle es aus und baue dein Business auch offline.',
    features: [
      '10 strukturierte Schritt-Seiten',
      'Interaktive Tools & Prompts',
      'Aufgaben mit Checkboxen',
      'Notiz- & Reflexionsseiten',
    ],
    btn: 'PDF-Workbook herunterladen',
    btnDone: 'Heruntergeladen!',
    btnLoading: 'PDF wird erstellt…',
    guestBtn: 'Kostenloses Workbook holen',
    guestHint: 'Kostenlose Registrierung — dauert 30 Sekunden',
    pages: '14 Seiten · A4 · Druckfertig',
    modalTitle: 'Dein kostenloses Workbook',
    modalSubtitle: 'Erstelle einen kostenlosen Account und lade das 10-Schritte-Workbook sofort herunter.',
    downloadLabel: 'Download',
    signupLabel: 'Kostenlos registrieren',
  },
};

// ── Auth Modal (stable top-level component — never defined inside render) ───
const AuthModal = ({ open, onClose, lang }) => {
  const l = LABELS[lang] || LABELS.en;
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="workbook-auth-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={e => { if (e.target === e.currentTarget) onClose(); }}
        >
          <motion.div
            key="workbook-auth-card"
            initial={{ opacity: 0, scale: 0.92, y: 24 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.92, y: 24 }}
            transition={{ type: 'spring', damping: 22, stiffness: 300 }}
            className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-md overflow-hidden"
          >
            {/* Gradient header */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6 text-white relative">
              <button
                onClick={onClose}
                className="absolute top-4 right-4 w-8 h-8 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 transition-all"
                aria-label="Close"
              >
                <SafeIcon icon={FiX} className="text-sm" />
              </button>
              <div className="flex items-center space-x-3 mb-2">
                <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                  <SafeIcon icon={FiFileText} className="text-lg" />
                </div>
                <div>
                  <p className="font-black text-base">{l.modalTitle}</p>
                  <p className="text-blue-100 text-xs">{l.pages}</p>
                </div>
              </div>
              <p className="text-blue-100 text-sm mt-1">{l.modalSubtitle}</p>
            </div>

            {/* Embedded auth form */}
            <div className="p-6">
              <Auth embedded={true} initialIsLogin={false} />
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// ── PDF Mockup ───────────────────────────────────────────────────────────────
const PdfMockup = ({ lang, l }) => (
  <div className="relative flex justify-center">
    <div className="relative w-52">
      <div className="absolute top-3 left-3 w-full h-full bg-purple-200 rounded-2xl rotate-3 opacity-50" />
      <div className="absolute top-1.5 left-1.5 w-full h-full bg-blue-200 rounded-2xl rotate-1 opacity-60" />
      <div className="relative bg-gradient-to-br from-blue-600 to-purple-700 rounded-2xl p-6 shadow-2xl text-white">
        <div className="flex items-center justify-between mb-4">
          <span className="text-xs font-black uppercase tracking-widest opacity-70">STUP-ON</span>
          <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
            <SafeIcon icon={FiFileText} className="text-sm" />
          </div>
        </div>
        <p className="text-[10px] font-black uppercase tracking-widest opacity-60 mb-1">
          {lang === 'de' ? 'DEIN' : 'YOUR'}
        </p>
        <p className="text-2xl font-black leading-tight mb-1">
          {lang === 'de' ? '10-SCHRITTE' : '10-STEP'}
        </p>
        <p className="text-xl font-black leading-tight mb-5">WORKBOOK</p>
        <div className="grid grid-cols-5 gap-1 mb-4">
          {Array.from({ length: 10 }, (_, i) => (
            <div
              key={i}
              className="bg-white/20 rounded-md h-5 flex items-center justify-center text-[8px] font-black"
            >
              {i + 1}
            </div>
          ))}
        </div>
        <div className="border-t border-white/20 pt-3">
          <p className="text-[8px] opacity-60 font-medium">{l.pages}</p>
        </div>
      </div>
    </div>
  </div>
);

// ── Main component ───────────────────────────────────────────────────────────
const WorkbookDownload = ({ user: userProp, compact = false }) => {
  const { lang } = useLanguage();
  const l = LABELS[lang] || LABELS.en;

  // Use internal session state so the component is always self-aware
  const [user, setUser] = useState(userProp ?? null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  // Flag: trigger download as soon as user logs in via the modal
  const [pendingDownload, setPendingDownload] = useState(false);

  // Keep user in sync when prop changes
  useEffect(() => {
    setUser(userProp ?? null);
  }, [userProp]);

  // Listen to auth changes (works even when prop is not updated by parent)
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const loggedInUser = session?.user ?? null;
      setUser(loggedInUser);
      if (loggedInUser && showAuthModal) {
        setShowAuthModal(false);
        setPendingDownload(true); // auto-download after login
      }
    });
    return () => subscription.unsubscribe();
  }, [showAuthModal]);

  // Auto-download once user is set after modal login
  useEffect(() => {
    if (pendingDownload && user) {
      setPendingDownload(false);
      triggerDownload();
    }
  }, [pendingDownload, user]);

  const triggerDownload = useCallback(async () => {
    if (loading || done) return;
    setLoading(true);
    await new Promise(r => setTimeout(r, 150));
    generateWorkbookPDF(lang);
    setLoading(false);
    setDone(true);
    setTimeout(() => setDone(false), 5000);
  }, [loading, done, lang]);

  const handleClick = () => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }
    triggerDownload();
  };

  const btnIcon = loading ? FiLoader : done ? FiCheckCircle : user ? FiDownload : FiLock;
  const btnLabel = loading
    ? (compact ? '…' : l.btnLoading)
    : done
    ? l.btnDone
    : user
    ? (compact ? l.downloadLabel : l.btn)
    : (compact ? l.signupLabel : l.guestBtn);

  // ── Compact banner ───────────────────────────────────────────────────────
  if (compact) {
    return (
      <>
        <AuthModal open={showAuthModal} onClose={() => setShowAuthModal(false)} lang={lang} />
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-5 flex flex-wrap items-center justify-between gap-4 shadow-lg"
        >
          <div className="flex items-center space-x-3 min-w-0">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
              <SafeIcon icon={FiFileText} className="text-white text-lg" />
            </div>
            <div className="min-w-0">
              <p className="text-white font-black text-sm truncate">{l.title}</p>
              <p className="text-blue-100 text-xs">{l.pages}</p>
            </div>
          </div>

          <button
            onClick={handleClick}
            disabled={loading || done}
            className={`flex items-center space-x-2 px-5 py-2.5 rounded-xl font-black text-sm whitespace-nowrap transition-all active:scale-95 shadow-md disabled:opacity-80 ${
              done ? 'bg-green-400 text-white' : 'bg-white text-blue-600 hover:bg-blue-50'
            }`}
          >
            <SafeIcon icon={btnIcon} className={loading ? 'animate-spin' : ''} />
            <span>{btnLabel}</span>
          </button>
        </motion.div>
      </>
    );
  }

  // ── Full card ────────────────────────────────────────────────────────────
  return (
    <>
      <AuthModal open={showAuthModal} onClose={() => setShowAuthModal(false)} lang={lang} />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden bg-white rounded-[2.5rem] shadow-xl border border-gray-100"
      >
        {/* Accent bar */}
        <div className="h-2 bg-gradient-to-r from-blue-600 to-purple-600" />

        <div className="p-8 md:p-10">
          {/* Badge */}
          <div className="inline-flex items-center space-x-2 bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-xs font-black uppercase tracking-widest mb-6">
            <SafeIcon icon={FiStar} />
            <span>{l.badge}</span>
          </div>

          <div className="grid md:grid-cols-2 gap-10 items-center">
            {/* Left */}
            <div>
              <h2 className="text-3xl md:text-4xl font-black text-gray-900 leading-tight mb-4 tracking-tight">
                {l.title}
              </h2>
              <p className="text-gray-600 font-medium leading-relaxed mb-8">{l.subtitle}</p>

              <ul className="space-y-3 mb-8">
                {l.features.map((f, i) => (
                  <li key={i} className="flex items-center space-x-3">
                    <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <SafeIcon icon={FiCheckCircle} className="text-green-600 text-xs" />
                    </div>
                    <span className="text-gray-700 font-bold text-sm">{f}</span>
                  </li>
                ))}
              </ul>

              {/* CTA */}
              <button
                onClick={handleClick}
                disabled={loading || done}
                className={`group flex items-center space-x-3 px-8 py-4 rounded-2xl font-black text-base shadow-lg transition-all active:scale-95 disabled:opacity-80 ${
                  done
                    ? 'bg-green-500 text-white shadow-green-200'
                    : 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:shadow-xl hover:-translate-y-0.5 shadow-blue-200'
                }`}
              >
                <SafeIcon
                  icon={btnIcon}
                  className={loading ? 'animate-spin' : 'group-hover:scale-110 transition-transform'}
                />
                <span>{btnLabel}</span>
              </button>

              {/* Guest hint */}
              {!user && !done && (
                <p className="mt-3 text-xs text-gray-400 font-medium flex items-center space-x-1">
                  <SafeIcon icon={FiLock} className="text-gray-300" />
                  <span>{l.guestHint}</span>
                </p>
              )}
            </div>

            {/* Right – PDF mockup */}
            <PdfMockup lang={lang} l={l} />
          </div>
        </div>
      </motion.div>
    </>
  );
};

export default WorkbookDownload;